import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './Sidebar.css';
import axios from 'axios';
const API_URL = process.env.REACT_APP_API_URL;

const Sidebar = ({ collapsed, mobileOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [profilePicture, setProfilePicture] = useState('');
  const [headline, setHeadline] = useState('');
  const [locationText, setLocationText] = useState('');
  const [currentCompany, setCurrentCompany] = useState('');
  const [completion, setCompletion] = useState(0);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const getMenuItems = () => {
    const role = user?.role;
    if (role === 'admin' || role === 'super_admin' || role === 'subadmin') {
      return [
        { icon: 'dashboard', label: 'Dashboard', path: '/admin/dashboard' },
        { icon: 'group', label: 'Users', path: '/admin/users' },
        { icon: 'verified', label: 'Verifications', path: '/admin/verifications' },
        { icon: 'business', label: 'Companies', path: '/admin/companies' },
        { icon: 'tune', label: 'Master Data', path: '/admin/master-data' },
      ];
    }
    if (role === 'employer') {
      return [
        { icon: 'dashboard', label: 'Dashboard', path: '/employer/dashboard' },
        { icon: 'person_search', label: 'Find Candidates', path: '/employer/find-candidates' },
        { icon: 'work', label: 'My Jobs', path: '/employer/my-jobs' },
        { icon: 'description', label: 'Applications', path: '/employer/applications' },
        { icon: 'business', label: 'Company Profile', path: '/employer/profile' },
        { icon: 'support_agent', label: 'Consultant Req.', path: '/employer/consultant-requests' },
      ];
    }
    if (role === 'consultant') {
      return [
        { icon: 'dashboard', label: 'Dashboard', path: '/consultant/dashboard' },
        { icon: 'person_search', label: 'Find Candidates', path: '/consultant/find-candidates' },
        { icon: 'group_add', label: 'My Candidates', path: '/consultant/my-candidates' },
        { icon: 'person', label: 'My Profile', path: '/consultant/profile' },
        { icon: 'corporate_fare', label: 'Companies', path: '/consultant/companies' },
        { icon: 'work', label: 'Jobs', path: '/consultant/jobs' },
        /* { icon: 'add_circle',     label: 'Create Job',   path: '/consultant/jobs/create' }, */
        { icon: 'description', label: 'Applications', path: '/consultant/applications' },
      ];
    }
    if (role === 'candidate') {
      return [
        { icon: 'dashboard', label: 'Dashboard', path: '/candidate/dashboard' },
        { icon: 'search', label: 'Browse Jobs', path: '/jobs' },
        { icon: 'description', label: 'My Applications', path: '/candidate/my-applications' },
        { icon: 'bookmark', label: 'Saved Jobs', path: '/candidate/saved-jobs' },
        { icon: 'person', label: 'Profile', path: '/candidate/profile' },
      ];
    }
    return [];
  };

  const menuItems = getMenuItems();

  useEffect(() => {
    const fetchCandidateData = async () => {
      if (user?.role === 'candidate') {
        try {
          const token = localStorage.getItem('token');
          const [profileRes, completionRes] = await Promise.all([
            axios.get(`${API_URL}/candidate/profile`, {
              headers: { Authorization: `Bearer ${token}` }
            }),
            axios.get(`${API_URL}/candidate/profile/completion`, {
              headers: { Authorization: `Bearer ${token}` }
            })
          ]);
          const profileData = profileRes.data?.data || {};
          setProfilePicture(profileData.profilePicture || '');
          setHeadline(profileData.headline || '');

          const currentLocation = profileData.currentLocation || {};
          setLocationText([currentLocation.city, currentLocation.state, currentLocation.country].filter(Boolean).join(', '));

          const experiences = profileData.workExperience || [];
          const currentWork = experiences.find(w => w.currentlyWorking) || experiences[0];
          setCurrentCompany(currentWork?.company || '');

          setCompletion(completionRes.data?.data?.percentage || 0);
        } catch (error) {
          console.error('Error fetching candidate data:', error);
        }
      }
    };
    fetchCandidateData();
  }, [user]);

  const roleLabel = {
    admin: 'Admin', super_admin: 'Super Admin', subadmin: 'Sub Admin',
    employer: 'Employer', candidate: 'Candidate', consultant: 'Consultant'
  };

  return (
    <>
      {mobileOpen && <div className="sidebar-overlay" onClick={onClose} />}
      <aside className={`sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
        {/* Logo */}
        <div className="sidebar-logo">
          <Link to="/" className="sidebar-logo-link" onClick={onClose}>
            <img src="/images/logo-footer.png" alt="HiringOpen" className="sidebar-logo-img" />
          </Link>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          <ul className="sidebar-list">
            {menuItems.map((item) => (
              <li key={item.path} className="sidebar-item">
                <Link
                  to={item.path}
                  className={`sidebar-link ${isActive(item.path) ? 'active' : ''}`}
                  onClick={onClose}
                  title={collapsed ? item.label : ''}
                >
                  <span className="sidebar-icon">
                    <span className="material-symbols-outlined">{item.icon}</span>
                  </span>
                  {!collapsed && <span className="sidebar-label">{item.label}</span>}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* User profile - only for candidate */}
        {user?.role === 'candidate' && (
          <div className="sidebar-profile">
            {!collapsed && (
              <button
                className="sidebar-edit-btn"
                onClick={() => { navigate(`/${user?.role}/profile`); onClose(); }}
                title="Edit Profile"
              >
                <span className="material-symbols-outlined">edit</span>
              </button>
            )}
            <div className="sidebar-avatar-ring">
              <svg viewBox="0 0 100 100" className="sidebar-progress-ring">
                <circle className="sidebar-progress-track" cx="50" cy="50" r="44" />
                <circle
                  className="sidebar-progress-bar"
                  cx="50"
                  cy="50"
                  r="44"
                  transform="rotate(-90 50 50)"
                  style={{
                    strokeDasharray: `${(2 * Math.PI * 44) * (completion / 100)} ${2 * Math.PI * 44}`
                  }}
                />
              </svg>
              <div className="sidebar-avatar sidebar-avatar-lg">
                {profilePicture ? (
                  <img src={profilePicture} alt={user?.name} className="sidebar-avatar-img" />
                ) : (
                  user?.name?.charAt(0).toUpperCase()
                )}
              </div>
            </div>
            {!collapsed && (
              <div className="sidebar-profile-info">
                <div className="sidebar-profile-name">{user?.name}</div>
                {headline && <div className="sidebar-headline">{headline}</div>}
                {(locationText || currentCompany) && (
                  <div className="sidebar-profile-meta">
                    {locationText && (
                      <div className="sidebar-meta-row">
                        <span className="material-symbols-outlined sidebar-meta-icon">location_on</span>
                        <span>{locationText}</span>
                      </div>
                    )}
                    {currentCompany && (
                      <div className="sidebar-meta-row">
                        <span className="material-symbols-outlined sidebar-meta-icon">work</span>
                        <span>{currentCompany}</span>
                      </div>
                    )}
                  </div>
                )}
                {completion > 0 && (
                  <div className="sidebar-completion">{completion}% Complete</div>
                )}
              </div>
            )}
          </div>
        )}

        {/* User footer */}
        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="sidebar-avatar">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            {!collapsed && (
              <div className="sidebar-user-info">
                <div className="sidebar-user-name">{user?.name}</div>
                <div className="sidebar-user-role">{roleLabel[user?.role] || user?.role}</div>
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
